// apps/web/src/features/commercial-proposals/sheet/CommercialProposalCustomerSheet.tsx
//
// @aiPurpose the single reusable customer-facing KP sheet renderer (#1217),
//   shared by the public sheet (`PublicProposalSheet`) and the internal
//   operator preview (`CommercialProposalPreview`) so both surfaces render
//   the IDENTICAL document from the same `CommercialProposalPublicDto`.
//   The photo and any action affordance are injected as slots so this
//   component performs NO data fetching and knows nothing about public vs
//   authenticated transport.
// @aiSecurity all customer-facing Russian copy comes from the DTO values or
//   the shared `COMMERCIAL_PROPOSAL_SHEET_CHROME` constants — never inlined
//   here. Notes preserve newlines via CSS `white-space: pre-wrap`, never
//   `dangerouslySetInnerHTML`.
import type { ReactNode } from 'react';
import {
  COMMERCIAL_PROPOSAL_SHEET_CHROME,
  COMMERCIAL_PROPOSAL_CONTACT_LINKS,
  type CommercialProposalPublicDto,
} from '@magimp/shared';
import { useTranslation } from 'react-i18next';

import { SheetCostColumn } from './SheetCostColumn.js';

const chrome = COMMERCIAL_PROPOSAL_SHEET_CHROME;
const NARROW_NO_BREAK_SPACE = ' ';

// Stable test/id slugs aligned by index with COMMERCIAL_PROPOSAL_CONTACT_LINKS
// (do NOT slug the Cyrillic labels). WHY: deterministic data-testids for e2e.
const CONTACT_LINK_SLUGS = ['telegram', 'avito', 'site'] as const;

export interface CommercialProposalCustomerSheetProps {
  dto: CommercialProposalPublicDto;
  /** Public: token-gated photo; internal: saved-photo preview; null when none. */
  photoSlot: ReactNode | null;
  /** Public: PDF download button; internal: undefined. */
  actionsSlot?: ReactNode;
  /** Public passes `cp-public-grand-total` for the grand value cell. */
  grandTotalTestId?: string;
}

export function CommercialProposalCustomerSheet({
  dto,
  photoSlot,
  actionsSlot,
  grandTotalTestId,
}: CommercialProposalCustomerSheetProps) {
  const { t } = useTranslation('commercialProposalsPublic');
  // #1410: a combined KP has BOTH cost sections; a single-mode KP has exactly
  // one (the projection blanks the hidden section's rows to []).
  const isCombined = dto.buyoutLines.length > 0 && dto.logisticsLines.length > 0;
  return (
    <div className="kp-sheet">
      <div className="kp-masthead">
        <span className="kp-masthead-brand">{chrome.brand}</span>
        <span className="kp-masthead-meta" data-testid="cp-masthead-meta">
          <span>{chrome.title}</span>
          <span>{dto.proposalDate}</span>
        </span>
      </div>

      <header className="kp-hero">
        <h1 className="kp-hero-title">{chrome.title}</h1>
        <div className="kp-hero-meta">
          {dto.client ? <Meta label={t('fields.client')} value={dto.client} /> : null}
          <Meta label={t('fields.proposalDate')} value={dto.proposalDate} testId="cp-hero-date" />
          <Meta label={t('fields.cnyRate')} value={dto.cnyRateDisplay} numeric />
          <Meta label={t('fields.usdRate')} value={dto.usdRateDisplay} numeric />
        </div>
        {dto.validity ? (
          <p className="kp-validity" data-testid="cp-validity">
            {dto.validity}
          </p>
        ) : null}
      </header>

      <div className="kp-prod-band">
        <div className="kp-prod-photo">{photoSlot}</div>
        <div>
          <h2 className="kp-prod-name" data-testid="cp-product-name">
            {dto.productName}
          </h2>
          {dto.characteristics.length > 0 ? (
            <ul className="kp-prod-specs">
              {dto.characteristics.map((line, i) => (
                <li key={`${i}-${line}`}>{line}</li>
              ))}
            </ul>
          ) : null}
          <span className="kp-badge">{dto.paymentSchemeBadge}</span>
        </div>
      </div>

      {/* #1410: render a column only for a non-empty (active) section; a
          single-mode KP shows ONE half-width column (the `kp-cols-single`
          modifier drops the divider border so the lone column reads cleanly). */}
      <div className={`kp-cols${isCombined ? '' : ' kp-cols-single'}`}>
        {dto.buyoutLines.length > 0 ? (
          <SheetCostColumn
            variant="buy"
            title={chrome.buyoutCaption}
            term={dto.buyoutTerm}
            lines={dto.buyoutLines}
          />
        ) : null}
        {dto.logisticsLines.length > 0 ? (
          <SheetCostColumn
            variant="log"
            title={dto.logisticsTitle}
            term={dto.logisticsTerm}
            lines={dto.logisticsLines}
          />
        ) : null}
      </div>

      <div className="kp-grand">
        <div>
          <div className="kp-grand-caption">
            {isCombined ? chrome.grandCaption : chrome.grandCaptionSingle}
          </div>
          {isCombined ? (
            <div className="kp-grand-subtotals">
              <span>{dto.buyTotalDisplay}</span>
              <span>{dto.logisticsTotalDisplay}</span>
            </div>
          ) : null}
          <div className="kp-grand-scheme">{dto.paymentSchemeBadge}</div>
        </div>
        <div
          className="kp-grand-value"
          data-testid={grandTotalTestId}
          role="img"
          aria-label={dto.grandTotalDisplay}
        >
          <GrandTotalValue value={dto.grandTotalDisplay} />
        </div>
      </div>

      {dto.buyoutNotes || dto.logisticsNotes ? (
        <div className="kp-notes">
          {dto.buyoutNotes ? (
            <div>
              <div className="kp-notes-head">{chrome.buyoutNotesHeader}</div>
              <p className="kp-notes-body">{dto.buyoutNotes}</p>
            </div>
          ) : null}
          {dto.logisticsNotes ? (
            <div>
              <div className="kp-notes-head">{chrome.logisticsNotesHeader}</div>
              <p className="kp-notes-body">{dto.logisticsNotes}</p>
            </div>
          ) : null}
        </div>
      ) : null}

      {dto.managerName || dto.managerPhone ? (
        <div className="kp-sign">
          <span className="kp-sign-caption">{chrome.signatureCaption}</span>
          {dto.managerName ? <span className="kp-sign-name">{dto.managerName}</span> : null}
          {dto.managerPhone ? <span className="kp-sign-phone">{dto.managerPhone}</span> : null}
        </div>
      ) : null}

      <section className="kp-contacts" data-testid="cp-contacts">
        <div className="kp-contacts-head">{COMMERCIAL_PROPOSAL_CONTACT_LINKS.heading}</div>
        <div className="kp-contacts-row">
          {COMMERCIAL_PROPOSAL_CONTACT_LINKS.links.map(({ label, url }, i) => (
            <a
              key={url}
              className="kp-contact-link"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${label} — ${COMMERCIAL_PROPOSAL_CONTACT_LINKS.newTabHint}`}
              data-testid={`cp-contact-link-${CONTACT_LINK_SLUGS[i]}`}
            >
              <span>{label}</span>
              <span aria-hidden="true"> ↗</span>
            </a>
          ))}
        </div>
      </section>

      {actionsSlot ? <div className="kp-actions">{actionsSlot}</div> : null}
    </div>
  );
}

function GrandTotalValue({ value }: { value: string }) {
  const parts = value.split(NARROW_NO_BREAK_SPACE);
  if (parts.length < 2) return value;
  const currency = parts.pop();
  return (
    <span aria-hidden="true">
      <span className="kp-grand-number-groups">
        {parts.map((part, index) => (
          <span key={`${index}-${part}`}>{part}</span>
        ))}
      </span>
      {NARROW_NO_BREAK_SPACE}
      {currency}
    </span>
  );
}

function Meta({
  label,
  value,
  numeric,
  testId,
}: {
  label: string;
  value: string;
  numeric?: boolean;
  testId?: string;
}) {
  return (
    <div>
      <div className="kp-meta-key">{label}</div>
      <div className={`kp-meta-val${numeric ? ' kp-meta-num' : ''}`} data-testid={testId}>
        {value}
      </div>
    </div>
  );
}
